import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import styles from './Modal.module.css';
export const Modal = ({ isOpen, onClose, title, subtitle, children, className, bodyClassName, style, closeOnBackdropClick = true, showCloseButton = true, closeIcon, closeButtonAriaLabel = 'Fechar modal', renderInPortal = false, 'data-testid': testId = 'modal', }) => {
    const modalRef = useRef(null);
    useEffect(() => {
        if (!isOpen)
            return;
        const body = document.body;
        const current = Number(body.getAttribute('data-open-modals') ?? '0');
        body.setAttribute('data-open-modals', String(current + 1));
        return () => {
            const now = Number(body.getAttribute('data-open-modals') ?? '1');
            const next = Math.max(0, now - 1);
            if (next === 0) {
                body.removeAttribute('data-open-modals');
            }
            else {
                body.setAttribute('data-open-modals', String(next));
            }
        };
    }, [isOpen]);
    useEffect(() => {
        const handleEscapeKey = (e) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscapeKey);
            const timeoutId = setTimeout(() => {
                if (modalRef.current) {
                    const hasInputs = modalRef.current.querySelectorAll('input, textarea, select').length > 0;
                    if (!hasInputs) {
                        modalRef.current.focus();
                    }
                }
            }, 0);
            return () => {
                document.removeEventListener('keydown', handleEscapeKey);
                clearTimeout(timeoutId);
            };
        }
        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isOpen, onClose]);
    if (typeof isOpen !== 'boolean') {
        return null;
    }
    if (typeof onClose !== 'function') {
        return null;
    }
    if (!isOpen)
        return null;
    const handleBackdropClick = (e) => {
        try {
            if (closeOnBackdropClick && e.target === e.currentTarget) {
                onClose();
            }
        }
        catch (error) {
            return null;
        }
    };
    const handleBackdropKeyDown = (e) => {
        try {
            if ((e.key === 'Enter' || e.key === ' ') && closeOnBackdropClick && e.target === e.currentTarget) {
                e.preventDefault();
                onClose();
            }
        }
        catch (error) {
            return null;
        }
    };
    const combinedClassName = [styles.modal, className].filter(Boolean).join(' ');
    const combinedBodyClassName = [styles.body, bodyClassName].filter(Boolean).join(' ');
    const content = (_jsx("div", { "data-testid": `${testId}-backdrop`, className: styles.backdrop, ...(closeOnBackdropClick && {
            onClick: handleBackdropClick,
            onKeyDown: handleBackdropKeyDown,
            role: 'button',
            tabIndex: 0,
            'aria-label': 'Fechar modal',
        }), ...(!closeOnBackdropClick && {
            'aria-hidden': 'true',
        }), children: _jsxs("dialog", { ref: modalRef, "data-testid": `${testId}-content`, className: combinedClassName, style: style, open: true, "aria-labelledby": title ? `${testId}-title` : undefined, "aria-describedby": `${testId}-content`, children: [(title || subtitle || showCloseButton) && (_jsxs("div", { "data-testid": `${testId}-header`, className: styles.header, children: [(title || subtitle) && (_jsxs("div", { className: styles.titleContainer, children: [title && (_jsx("div", { "data-testid": `${testId}-title`, className: styles.title, children: title })), subtitle && (_jsx("div", { "data-testid": `${testId}-subtitle`, className: styles.subtitle, children: subtitle }))] })), showCloseButton && (_jsx("button", { "data-testid": `${testId}-close-button`, className: styles.closeButton, onClick: onClose, "aria-label": closeButtonAriaLabel, children: closeIcon || (_jsx("svg", { width: '16', height: '16', viewBox: '0 0 16 16', fill: 'none', xmlns: 'http://www.w3.org/2000/svg', children: _jsx("path", { d: 'M12 4L4 12M4 4L12 12', stroke: 'currentColor', strokeWidth: '1.5', strokeLinecap: 'round', strokeLinejoin: 'round' }) })) }))] })), _jsx("div", { "data-testid": `${testId}-body`, className: combinedBodyClassName, children: children })] }) }));
    return renderInPortal ? createPortal(content, document.body) : content;
};
export default Modal;
